import { HttpClient } from "@angular/common/http";
import { effect, inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { MessageDetails, MessageDraft, MessagePreview } from "../models/message";
import { ScreenStore, UserStore } from "../stores/index";
import { ColDef } from "ag-grid-community";
import { MessageOpenedRenderer } from "../table-renderers/message-opened";
import { SecondsToDateRenderer } from "../table-renderers/seconds-to-date";
import { JhmTableRepository } from "../components/jhm-table/jhm-table.component";

@Injectable()
export class SharedMessageRepositoryTable implements OnDestroy, JhmTableRepository
{
	private requestMapping: string = "messages";
	private http: HttpClient;
	public inboxOrSent: string | undefined | null;
	public userId: string | undefined | null;
	public ready: BehaviorSubject<boolean>;
	public userType: string;
	public isMobile: boolean = false;
	public ngDestroyed$: Subject<any> = new Subject();

	public colDefs: ColDef[];

	public inboxColDefs: ColDef[] = [
		{ headerName: "", field: "openedIcon", cellRenderer: MessageOpenedRenderer, width: 50, maxWidth: 50 },
		{ headerName: "From", field: "senderFullName", filter: true },
		{ headerName: "Subject", field: "subject", flex: 2 },
		{ headerName: "Date", field: "sentSecondsFromEpoch", filter: true, cellRenderer: SecondsToDateRenderer },
		{ headerName: "", field: "delete", width: 50, maxWidth: 50,
			cellRenderer: function(params: any) {
				return "<i class=\"fa-light fa-trash-alt\"></i>";
			},
			resizable: false
		},
		{ headerName: "Body", field: "body", hide: true }
	];

	public mobileInboxColDefs: ColDef[] = [
		{ headerName: "", field: "openedIcon", cellRenderer: MessageOpenedRenderer, width: 50, maxWidth: 50 },
		{ headerName: "From/Subject", field: "senderFullName" },
		{ headerName: "Date", field: "sentSecondsFromEpoch", filter: true, cellRenderer: SecondsToDateRenderer },
		{ headerName: "", field: "delete", width: 50, maxWidth: 50,
			cellRenderer: function(params: any) {
				return "<i class=\"fa-light fa-trash-alt\"></i>";
			},
			resizable: false
		},
		{ headerName: "Body", field: "body", hide: true }
	];

	public draftColDefs: ColDef[] = [
		{ headerName: "To", field: "recipientFullName", filter: true },
		{ headerName: "Subject", field: "subject", flex: 2 },
		{ headerName: "", field: "delete", width: 50, maxWidth: 50,
			cellRenderer: function(params: any) {
				return "<i class=\"fa-light fa-trash-alt\"></i>";
			},
			resizable: false
		},
		{ headerName: "Body", field: "body", hide: true }
	];

	public sentColDefs: ColDef[] = [
		{ headerName: "To", field: "recipientFullName", filter: true },
		{ headerName: "Subject", field: "subject", flex: 2 },
		{ headerName: "Date", field: "sentSecondsFromEpoch", filter: true, cellRenderer: SecondsToDateRenderer },
		{ headerName: "", field: "delete", width: 50, maxWidth: 50,
			cellRenderer: function(params: any) {
				return "<i class=\"fa-light fa-trash-alt\"></i>";
			},
			resizable: false
		},
		{ headerName: "Body", field: "body", hide: true }
	];

	public mobileSentColDefs: ColDef[] = [
		{ headerName: "To", field: "recipientFullName", filter: true },
		{ headerName: "Date", field: "sentSecondsFromEpoch", filter: true, cellRenderer: SecondsToDateRenderer },
		{ headerName: "", field: "delete", width: 50, maxWidth: 50,
			cellRenderer: function(params: any) {
				return "<i class=\"fa-light fa-trash-alt\"></i>";
			},
			resizable: false
		},
		{ headerName: "Body", field: "body", hide: true }
	];

	readonly screenStore = inject(ScreenStore);
	readonly userStore = inject(UserStore);
	public screenSize: string;

	public constructor(http: HttpClient)
	{
		this.http = http;
		this.ready = new BehaviorSubject<boolean>(false);

		effect(() => {
			this.userType = this.userStore.type();
			this.userId = this.userStore.userId();
			this.tableReady();
		})

		effect(() => {
			this.screenSize = this.screenStore.screenSize();
			if ("MOBILE" === this.screenSize)
			{
				this.isMobile = true;
			}
			else {
				this.isMobile = false;
			}
		})
	}

	tableReady(): void {
		if (this.userId) {
			this.ready.next(true);
		}
	}

	ngOnDestroy(): void
	{
		this.ngDestroyed$.next(true);
	}

	public setRepositoryPurpose(purpose: string): void
	{
		if (purpose === "INBOX")
		{
			this.colDefs = this.isMobile ? this.mobileInboxColDefs : this.inboxColDefs;
			this.inboxOrSent = "inbox";
		}
		else if (purpose === "DRAFTS")
		{
			this.colDefs = this.draftColDefs;
			this.inboxOrSent = "drafts";
		}
		else if (purpose === "SENT")
		{
			this.colDefs = this.isMobile ? this.mobileSentColDefs : this.sentColDefs;
			this.inboxOrSent = "sent";
		}
	}

	public trashClickFunction(message: MessagePreview): void
	{
		// This loses context, we'll need to think through this.
		this.deleteDraft(message.id).subscribe();
	}

	public trashIconFunction(message: MessagePreview): string
	{
		return "fa-light fa-trash-alt";
	}

	public fetchAll(): Observable<MessagePreview[]>
	{
		return this.http.get<MessagePreview[]>(`${this.requestMapping}/users/${this.userId}/${this.inboxOrSent}`);
	}

	public getMessageDetails(messageId: string): Observable<MessageDetails>
	{
		return this.http.get<MessageDetails>(`${this.requestMapping}/${messageId}`);
	}

	public getMessageDraft(messageId: string): Observable<MessageDraft>
	{
		return this.http.get<MessageDraft>(`${this.requestMapping}/drafts/${messageId}`);
	}

	public createMessageDraft(recipientId: string = null): Subject<Observable<MessageDraft>>
	{
		const subject: Subject<Observable<MessageDraft>> = new Subject<Observable<MessageDraft>>();
		const draft: MessageDraft = new MessageDraft();

		if (recipientId)
		{
			draft.recipientId = recipientId;
		}

		if (this.userType === "CIVILIAN")
		{
			Radar.getLocation((err: any, result: any) =>
			{
				if (err)
				{
					Radar.ipGeocode((err2: any, result2: any) =>
					{
						if (err2)
						{
							subject.next(this.http.post<MessageDraft>(`${this.requestMapping}/drafts`, draft));
							subject.complete();
						}
						else
						{
							draft.latitude = result2.address.latitude;
							draft.longitude = result2.address.longitude;
							subject.next(this.http.post<MessageDraft>(`${this.requestMapping}/drafts`, draft));
							subject.complete();
						}
					});
				}
				else
				{
					draft.latitude = result.location.latitude;
					draft.longitude = result.location.longitude;
					subject.next(this.http.post<MessageDraft>(`${this.requestMapping}/drafts`, draft));
					subject.complete();
				}
			});
		}
		else
		{
			// Need this here to put it to the end of the queue so the subject gets returned before next is called
			setTimeout(() =>
			{
				subject.next(this.http.post<MessageDraft>(`${this.requestMapping}/drafts`, draft));
				subject.complete();
			});
		}
		return subject;
	}

	public deleteDraft(messageId: string): Observable<void>
	{
		return this.http.delete<void>(`${this.requestMapping}/drafts/${messageId}`);
	}

	public deleteIncomingMessage(messageId: string): Observable<void>
	{
		return this.http.delete<void>(`${this.requestMapping}/inbox/${messageId}`);
	}

	public deleteSentMessage(messageId: string): Observable<void>
	{
		return this.http.delete<void>(`${this.requestMapping}/sent/${messageId}`);
	}

	public purchaseMessage(messageId: string): Observable<void>
	{
		return this.http.put<void>(`${this.requestMapping}/${messageId}/purchase`, {});
	}
}
